import React, { useEffect } from "react";
import {
  AUTH_LOCAL_STORAGE_KEY,
  parseJwtEncodedTokenAndGetReduxState,
} from "../../services/types/auth-types";
import { logoutUser, setAuthState } from "../../services/actions/auth-actions";
import { useDispatch } from "react-redux";
import { removeUserInfo } from "../../services/actions/user-actions";
import { clearWebs } from "../../services/actions/web-actions";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Routes } from "../../navigation/navigation-types";
import WelcomePng from "../../assets/images/welcome.png";
import DeletePng from "../../assets/images/delete.png";
import { Stack, Typography, CircularProgress } from "@mui/material";
import CustomButton from "../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { getAppLanguage } from "../../utils/languageHelper";

const ShopifyLogin: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();

  const [screenText, setScreenText] = React.useState<string>("");
  const [isError, setIsError] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const hasRunRef = React.useRef(false);

  // CRITICAL: Save token IMMEDIATELY on component mount, BEFORE any useEffect runs
  // This must run synchronously in render phase to beat MainNavigator's useEffect
  React.useMemo(() => {
    const token = searchParams.get("token");
    if (token && !hasRunRef.current) {
      localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, token);
      const authState = parseJwtEncodedTokenAndGetReduxState(token);
      dispatch(setAuthState(authState));
    }
  }, [searchParams, dispatch]);

  useEffect(() => {
    // Prevent running multiple times
    if (hasRunRef.current) return;
    hasRunRef.current = true;
    const removeOldToken = async () => {
      dispatch(removeUserInfo());
      dispatch(clearWebs());
      dispatch(logoutUser());
      localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY);
    };

    const login = async () => {
      setScreenText("");
      setIsError(false);
      setIsLoading(true);

      try {
        // Get token and shop from URL parameters FIRST
        let token = searchParams.get("token");
        const shop = searchParams.get("shop");
        const language = searchParams.get("language") || "cs";

        if (!token) {
          throw new Error("Missing token parameter");
        }

        // Store shop in localStorage for later use
        if (shop) {
          localStorage.setItem("shopify_shop", shop);
        }

        // Check if token is a Shopify session token (contains 'dest' or 'iss' claims)
        // If so, exchange it for our app JWT token
        const isShopifySessionToken = (token: string): boolean => {
          try {
            const parts = token.split('.');
            if (parts.length !== 3) return false;
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
            return !!payload.dest || !!payload.iss;
          } catch {
            return false;
          }
        };

        if (isShopifySessionToken(token)) {
          // Exchange Shopify session token for app JWT
          const serverUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
          const response = await fetch(
            `${serverUrl}/shopify/exchange-token?sessionToken=${encodeURIComponent(token)}&shop=${encodeURIComponent(shop || '')}`
          );

          if (!response.ok) {
            throw new Error(`Token exchange failed: ${response.status}`);
          }

          const data = await response.json();
          if (!data.token) {
            throw new Error('No token received from exchange');
          }
          token = data.token;
        }

        // Final validation
        if (!token) {
          throw new Error("No valid token available");
        }

        // IMMEDIATELY save token to localStorage and Redux BEFORE any other operations
        // This prevents race condition with MainNavigator/SidePanel mount
        localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, token);

        const authState = parseJwtEncodedTokenAndGetReduxState(token);

        // Set authentication state in Redux
        dispatch(setAuthState(authState));

        // Change language if provided
        i18n.changeLanguage(getAppLanguage(language));

        // Show success message
        setScreenText(
          t("shopifyFirstTouchScreen.title") || "Welcome to your dashboard!"
        );
        setIsLoading(false);

        // Navigate to campaigns if user is confirmed
        if (
          authState.parsedToken.isActivationConfirmed &&
          authState.parsedToken.registeredFrom === "shopify"
        ) {
          // Small delay to show welcome message
          setTimeout(() => {
            navigate(Routes.campaign);
          }, 1500);
        }
      } catch (error) {
        console.error("Shopify login error:", error);
        await removeOldToken();

        setIsError(true);
        setIsLoading(false);
        setScreenText(
          t("shopifyFirstTouchScreen.error") ||
            "Authentication failed. Please try again."
        );
      }
    };

    login();
  }, [dispatch, navigate, searchParams, i18n, t]);

  const handleButtonClick = async () => {
    navigate(Routes.profile, {
      state: { preselectedTab: "subscription" },
    });
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#f6f6f7",
      }}
    >
      <Stack px={20} justifyContent='center' alignItems='center'>
        {isLoading ? (
          <CircularProgress size={60} sx={{ color: "#008060" }} />
        ) : (
          <img
            src={isError ? DeletePng : WelcomePng}
            alt={isError ? "error" : "welcome"}
          />
        )}
        <Stack
          maxWidth={800}
          spacing={1}
          justifyContent='center'
          alignItems='center'
          textAlign='center'
          my={3}
        >
          <Typography fontSize={32} fontWeight={500}>
            {screenText}
          </Typography>
          {!isError && !isLoading && (
            <>
              <Typography>
                {t("shopifyFirstTouchScreen.description1") ||
                  "Your Shopify store has been successfully connected."}
              </Typography>
              <Typography>
                {t("shopifyFirstTouchScreen.description2") ||
                  "You can now create notification campaigns and start boosting your sales."}
              </Typography>
            </>
          )}
        </Stack>
        {!isError && !isLoading && (
          <CustomButton
            title={
              t("shopifyFirstTouchScreen.button") || "Go to Subscription"
            }
            onClick={handleButtonClick}
            size='medium'
            style={{
              paddingLeft: 20,
              paddingRight: 20,
              backgroundColor: "#008060",
            }}
          />
        )}
      </Stack>
    </div>
  );
};

export default ShopifyLogin;
